import { tt } from '@agos/shared'

/**
 * How close a pre-order is to its scheduled time.
 *
 * §14.8: a pre-order is attributed by `scheduledAt`, not by when it was taken.
 * Everything on this screen — the sort, the countdown, the "coming up" count —
 * reads that field, and getting it wrong puts an order in the wrong hour.
 */

const MINUTE_MS = 60_000
const SOON_MINUTES = 30
const UPCOMING_MINUTES = 60

export type PreorderUrgency = 'overdue' | 'soon' | 'upcoming' | 'ok'

export const URGENCY_CLASSES: Record<PreorderUrgency, string> = {
  overdue: 'bg-red-500/15 text-red-500',
  soon: 'bg-amber-500/15 text-amber-500',
  upcoming: 'bg-yellow-500/15 text-yellow-600',
  ok: 'bg-muted text-muted-foreground',
}

export function preorderUrgency(scheduledAt: string, now: number): PreorderUrgency {
  const remainingMs = new Date(scheduledAt).getTime() - now
  if (remainingMs < 0) return 'overdue'
  if (remainingMs < SOON_MINUTES * MINUTE_MS) return 'soon'
  if (remainingMs < UPCOMING_MINUTES * MINUTE_MS) return 'upcoming'
  return 'ok'
}

/** `za 1h 20m`, `za 5 min`, or the overdue shout. */
export function timeUntilScheduled(scheduledAt: string, now: number): string {
  const remainingMs = new Date(scheduledAt).getTime() - now
  if (remainingMs < 0) return tt('Po termíne!')

  const hours = Math.floor(remainingMs / (60 * MINUTE_MS))
  const minutes = Math.floor((remainingMs % (60 * MINUTE_MS)) / MINUTE_MS)
  return hours > 0 ? `${tt('za')} ${hours}h ${minutes}m` : `${tt('za')} ${minutes} min`
}

// ─── §9.343 - closing a preorder ──────────────────────────────────────────────────────────────────

/**
 * Why a preorder was closed, for the status log.
 *
 * **Derived, not asked** - §9.342's bar: derive when the datum is known, ask when it is not. A
 * storno's *why* is genuinely unknown to the system and is typed; a preorder being delivered is the
 * expected outcome, and the field usefully holds **where it was closed from**. That is provenance
 * rather than a fiction, and it is more than the plain `PATCH` recorded, which was nothing.
 */
export function preorderCloseReason(): string {
  return tt('Doručená z predobjednávok')
}
